import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@careflux/primitives';
import type { BreakdownTableProps } from './components.types';
import {
  SUPPRESSED_LABEL,
  UNKNOWN_LABEL,
  formatLatency,
  formatModeLabel,
  formatNumber,
  formatPercent,
} from './formatting';
import { emptyStateClasses } from './summaryQuality.styles';
import type { BreakdownDimension, BreakdownRow } from './types';

export const BREAKDOWN_DIMENSIONS: { value: BreakdownDimension; label: string }[] = [
  { value: 'org', label: 'Stofnun' },
  { value: 'user', label: 'Notandi' },
  { value: 'model', label: 'Líkan' },
  { value: 'prompt_hash', label: 'Útgáfa fyrirmæla' },
  { value: 'template', label: 'Sniðmát' },
  { value: 'mode', label: 'Tegund' },
];

// The mode dimension's raw label is 'conversation'/'dictation'; every other
// dimension's label already comes formatted for display (a name or its own value).
const rowLabel = (row: BreakdownRow, by: BreakdownDimension): string =>
  by === 'mode' ? formatModeLabel(row.label) : row.label || UNKNOWN_LABEL;

export const BreakdownTable = ({ rows, by, onByChange }: BreakdownTableProps) => (
  <Card padding="lg">
    <CardHeader>
      <CardTitle>Sundurliðun</CardTitle>
      <CardDescription>Breytingahlutfall og biðtími eftir vídd, raðað eftir fjölda samantekta</CardDescription>
      <CardAction>
        <Select value={by} onValueChange={(value) => onByChange(value as BreakdownDimension)}>
          <SelectTrigger className="w-[180px]" aria-label="Sundurliða eftir">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {BREAKDOWN_DIMENSIONS.map((dimension) => (
              <SelectItem key={dimension.value} value={dimension.value}>
                {dimension.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </CardAction>
    </CardHeader>
    <CardContent>
      {rows.length === 0 ? (
        <div className={emptyStateClasses}>Engin gögn fyrir valið tímabil.</div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{BREAKDOWN_DIMENSIONS.find((dimension) => dimension.value === by)?.label}</TableHead>
              <TableHead className="text-right">Samantektir</TableHead>
              <TableHead className="text-right">Mælingar</TableHead>
              <TableHead className="text-right">Meðalbreyting</TableHead>
              <TableHead className="text-right">Miðgildi</TableHead>
              <TableHead className="text-right">p95 biðtími</TableHead>
              <TableHead className="text-right">Varaleið</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={row.key ?? `unknown-${index}`}>
                <TableCell className="max-w-[240px] truncate">{rowLabel(row, by)}</TableCell>
                <TableCell className="text-right tabular-nums">{formatNumber(row.generations)}</TableCell>
                <TableCell className="text-right tabular-nums">{formatNumber(row.metricRows)}</TableCell>
                {row.suppressed ? (
                  <TableCell colSpan={2} className="text-right text-muted-foreground">
                    {SUPPRESSED_LABEL}
                  </TableCell>
                ) : (
                  <>
                    <TableCell className="text-right tabular-nums">{formatPercent(row.editRateMean)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatPercent(row.editRateMedian)}</TableCell>
                  </>
                )}
                <TableCell className="text-right tabular-nums">{formatLatency(row.latencyP95Ms)}</TableCell>
                <TableCell className="text-right tabular-nums">{formatPercent(row.fallbackRate)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </CardContent>
  </Card>
);
